/**
 * Frontend Error Utilities
 * 
 * Client-side error handling and user notifications.
 */

/**
 * Error codes from backend
 */
export enum ErrorCode {
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  AUTHORIZATION_ERROR = 'AUTHORIZATION_ERROR',
  NOT_FOUND_ERROR = 'NOT_FOUND_ERROR',
  CONFLICT_ERROR = 'CONFLICT_ERROR',
  EXTERNAL_SERVICE_ERROR = 'EXTERNAL_SERVICE_ERROR',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  RATE_LIMIT_ERROR = 'RATE_LIMIT_ERROR'
}

/**
 * API error response
 */
export interface ApiError {
  code: ErrorCode
  message: string
  details?: Record<string, unknown>
  field?: string
}

/**
 * Error response wrapper
 */
export interface ErrorResponse {
  success: false
  error: ApiError
}

/**
 * User-friendly error messages
 */
const ErrorMessages: Record<ErrorCode, string> = {
  [ErrorCode.VALIDATION_ERROR]: 'Tarkista lomakkeen tiedot',
  [ErrorCode.AUTHENTICATION_ERROR]: 'Log in again',
  [ErrorCode.AUTHORIZATION_ERROR]: 'Sinulla ei ole oikeuksia tähän toimintoon',
  [ErrorCode.NOT_FOUND_ERROR]: 'Pyydettyä tietoa ei löytynyt',
  [ErrorCode.CONFLICT_ERROR]: 'Tieto on jo olemassa',
  [ErrorCode.EXTERNAL_SERVICE_ERROR]: 'Ulkoinen palvelu ei vastaa',
  [ErrorCode.INTERNAL_ERROR]: 'Järjestelmässä tapahtui virhe',
  [ErrorCode.RATE_LIMIT_ERROR]: 'Liian monta pyyntöä. Yritä myöhemmin uudelleen'
}

/**
 * Get user-friendly error message
 */
export function getErrorMessage(error: ApiError | string): string {
  if (typeof error === 'string') {
    return error
  }
  
  return error.message || ErrorMessages[error.code] || 'Tuntematon virhe'
}

/**
 * Check if error is of specific type
 */
export function isErrorCode(error: unknown, code: ErrorCode): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    (error as ApiError).code === code
  )
}

/**
 * Handle API error
 */
export function handleApiError(error: unknown): ApiError {
  if (typeof error === 'object' && error !== null && 'error' in error) {
    return (error as ErrorResponse).error
  }
  
  return {
    code: ErrorCode.INTERNAL_ERROR,
    message: error instanceof Error ? error.message : 'Tuntematon virhe'
  }
}

/**
 * Toast notification types
 */
export type ToastType = 'error' | 'warning' | 'success' | 'info'

/**
 * Toast notification
 */
export interface Toast {
  id: string
  type: ToastType
  message: string
  duration?: number
}

/**
 * Toast store (simplified - integrate with your state management)
 */
class ToastStore {
  private listeners: ((toasts: Toast[]) => void)[] = []
  private toasts: Toast[] = []

  subscribe(listener: (toasts: Toast[]) => void) {
    this.listeners.push(listener)
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener)
    }
  }

  private notify() {
    this.listeners.forEach(l => l([...this.toasts]))
  }

  add(toast: Omit<Toast, 'id'>) {
    const id = Math.random().toString(36).substring(2, 9)
    this.toasts.push({ ...toast, id })
    this.notify()
    
    // Auto-remove after duration
    setTimeout(() => this.remove(id), toast.duration || 5000)
  }

  remove(id: string) {
    this.toasts = this.toasts.filter(t => t.id !== id)
    this.notify()
  }
}

export const toastStore = new ToastStore()

/**
 * Show error toast
 */
export function showError(message: string, duration?: number) {
  toastStore.add({ type: 'error', message, duration })
}

/**
 * Show success toast
 */
export function showSuccess(message: string, duration?: number) {
  toastStore.add({ type: 'success', message, duration })
}

/**
 * Show warning toast
 */
export function showWarning(message: string, duration?: number) {
  toastStore.add({ type: 'warning', message, duration })
}

/**
 * Show API error as toast
 */
export function showApiError(error: unknown) {
  const apiError = handleApiError(error)
  showError(getErrorMessage(apiError))
}
